/**
 * Reward categories shared by Manage Rewards and the Rewards Store.
 *
 * `rewards.category` is free text. Starter rewards use privileges,
 * screen_time, treats, social, purchases and activities; older data also has
 * singular or capitalised spellings (privilege, Treats, Screen Time). Keys are
 * normalised so those group together instead of becoming separate filters.
 */
export interface RewardCategory {
  value: string
  label: string
  emoji: string
}

export const REWARD_CATEGORIES: RewardCategory[] = [
  { value: 'privileges', label: 'Privileges', emoji: '⭐' },
  { value: 'screen_time', label: 'Screen Time', emoji: '📺' },
  { value: 'treats', label: 'Treats', emoji: '🍦' },
  { value: 'social', label: 'Social', emoji: '👫' },
  { value: 'activities', label: 'Activities', emoji: '🎨' },
  { value: 'purchases', label: 'Purchases', emoji: '🛍️' },
  { value: 'experience', label: 'Experiences', emoji: '🏆' },
  { value: 'item', label: 'Items', emoji: '🎁' },
  { value: 'money', label: 'Money', emoji: '💵' },
]

const ALIASES: Record<string, string> = {
  privilege: 'privileges',
  screentime: 'screen_time',
  treat: 'treats',
  activity: 'activities',
  purchase: 'purchases',
  experiences: 'experience',
  items: 'item',
  toys: 'item',
  toy: 'item',
  outings: 'experience',
  outing: 'experience',
}

/** Lower case, spaces and dashes to underscores, known aliases folded. */
export function categoryKey(category: string | null | undefined): string {
  const k = (category || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  return ALIASES[k] || ALIASES[k.replace(/_/g, '')] || k
}

/** Label and emoji for any stored category, known or not. */
export function categoryInfo(category: string | null | undefined): RewardCategory {
  const key = categoryKey(category)
  const known = REWARD_CATEGORIES.find((c) => c.value === key)
  if (known) return known
  if (!key) return { value: '', label: 'Other', emoji: '🎁' }
  const label = key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
  return { value: key, label, emoji: '🎁' }
}

/** The categories present in `rewards`, in the standard order, unknown ones last. */
export function categoriesIn(rewards: { category?: string | null }[]): RewardCategory[] {
  const keys = new Set(rewards.map((r) => categoryKey(r.category)))
  const known = REWARD_CATEGORIES.filter((c) => keys.has(c.value))
  const other = [...keys]
    .filter((k) => !REWARD_CATEGORIES.some((c) => c.value === k))
    .sort()
    .map((k) => categoryInfo(k))
  return [...known, ...other]
}
